import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import useSWR, { useSWRConfig } from "swr";
import {
  Table,
  Drawer,
  Descriptions,
  Divider,
  Select,
  Input,
  Button,
  Space,
  Tag,
  Checkbox,
  message,
  Popconfirm,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  getCommunityPosts,
  getCommunityPostDetail,
  lockCommunityPost,
  unlockCommunityPost,
  forceDeleteCommunityPost,
} from "../api/domainApi";
import { CmntyAdminPostListItem } from "../types/domain";
import PageHeader from "../components/common/PageHeader";
import PostCommentThread from "../components/community/PostCommentThread";
import {
  AuditLogTimeline,
  auditLogKey,
} from "../components/community/AuditLogTimeline";
import AdminAuthorModal from "../components/community/AdminAuthorModal";

const PAGE_SIZE = 20;

const CommunityPostPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [boardCode, setBoardCode] = useState<string | undefined>();
  const [keyword, setKeyword] = useState("");
  const [kwInput, setKwInput] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [authorOpen, setAuthorOpen] = useState(false);

  const swrKey = `/community/posts?p=${page}&b=${boardCode}&kw=${keyword}&del=${includeDeleted}`;
  const { data, isLoading, mutate } = useSWR(
    swrKey,
    () =>
      getCommunityPosts({
        page: page - 1,
        size: PAGE_SIZE,
        boardCode,
        keyword,
        includeDeleted,
      }),
    { keepPreviousData: true },
  );

  const { data: detail, mutate: mutateDetail } = useSWR(
    detailId ? `/community/posts/${detailId}` : null,
    () => getCommunityPostDetail(detailId!),
  );

  const openDetail = (id: number) => {
    setDetailId(id);
    setDrawerOpen(true);
  };

  const { mutate: globalMutate } = useSWRConfig();

  const act = async (fn: () => Promise<any>, ok: string, postId?: number) => {
    try {
      await fn();
      message.success(ok);
      mutate();
      mutateDetail();
      // 잠금/삭제는 이력이 한 건 늘어난다 — 열려 있는 이력 섹션도 같이 갱신한다.
      if (postId) globalMutate(auditLogKey("POST", postId));
    } catch (e: any) {
      message.error(e.message);
    }
  };

  const columns: ColumnsType<CmntyAdminPostListItem> = [
    { title: "ID", dataIndex: "postId", width: 70 },
    {
      title: "게시판",
      dataIndex: "boardCode",
      width: 130,
      render: (v, r) =>
        v === "TYPE" ? `유형 (${r.postTypeName ?? "-"})` : "자유",
    },
    { title: "제목", dataIndex: "title", ellipsis: true },
    {
      title: "작성자",
      dataIndex: "authorUserId",
      width: 140,
      render: (v: number, r) =>
        r.authorNickname ? `${r.authorNickname} (#${v})` : `#${v}`,
    },
    {
      title: "반응",
      width: 120,
      render: (_, r) => `♥${r.likeCnt} 💬${r.commentCnt} 👁${r.viewCnt}`,
    },
    {
      title: "상태",
      width: 120,
      render: (_, r) => (
        <>
          {r.delYn ? (
            <Tag color="red">삭제</Tag>
          ) : (
            <Tag color="green">노출</Tag>
          )}
          {r.adminLockYn && <Tag color="volcano">잠금</Tag>}
        </>
      ),
    },
    {
      title: "작성일",
      dataIndex: "createdAt",
      width: 130,
      render: (v: string) => (v ? v.replace("T", " ").slice(0, 16) : "-"),
    },
    {
      title: "관리",
      width: 290,
      render: (_, row) => (
        <Space size={4} wrap>
          <Button
            size="small"
            type="dashed"
            onClick={() => openDetail(row.postId)}
          >
            상세
          </Button>
          <Button
            size="small"
            onClick={() => navigate(`/community/comments?postId=${row.postId}`)}
          >
            댓글
          </Button>
          {row.adminLockYn ? (
            <Button
              size="small"
              onClick={() =>
                act(() => unlockCommunityPost(row.postId), "잠금 해제됨", row.postId)
              }
            >
              잠금해제
            </Button>
          ) : (
            <Button
              size="small"
              disabled={row.delYn}
              onClick={() =>
                act(() => lockCommunityPost(row.postId), "잠금됨", row.postId)
              }
            >
              잠금
            </Button>
          )}
          <Popconfirm
            title="게시글을 강제 삭제하시겠습니까?"
            onConfirm={() =>
              act(() => forceDeleteCommunityPost(row.postId), "삭제됨", row.postId)
            }
          >
            <Button size="small" danger disabled={row.delYn}>
              삭제
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="커뮤니티 게시글 관리" />
      {/* 상세에서 관리자 댓글을 쓰려면 명의가 먼저 등록돼 있어야 한다(미등록이면 서버가 404).
          댓글 관리 페이지까지 건너가지 않고 여기서 바로 등록·수정할 수 있게 둔다. */}
      <Space style={{ marginBottom: 12 }} wrap>
        <Button onClick={() => setAuthorOpen(true)}>관리자 프로필</Button>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          상세 &gt; 댓글에서 관리자 명의로 쓰려면 먼저 등록하세요.
        </Typography.Text>
      </Space>
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          placeholder="게시판 전체"
          allowClear
          style={{ width: 130 }}
          options={[
            { value: "FREE", label: "자유게시판" },
            { value: "TYPE", label: "유형게시판" },
          ]}
          value={boardCode}
          onChange={(v) => {
            setBoardCode(v);
            setPage(1);
          }}
        />
        <Input
          placeholder="제목/내용 검색"
          value={kwInput}
          onChange={(e) => setKwInput(e.target.value)}
          onPressEnter={() => {
            setKeyword(kwInput);
            setPage(1);
          }}
          style={{ width: 220 }}
        />
        <Button
          onClick={() => {
            setKeyword(kwInput);
            setPage(1);
          }}
        >
          검색
        </Button>
        <Checkbox
          checked={includeDeleted}
          onChange={(e) => {
            setIncludeDeleted(e.target.checked);
            setPage(1);
          }}
        >
          삭제 포함
        </Checkbox>
        <Button
          onClick={() => {
            setBoardCode(undefined);
            setKwInput("");
            setKeyword("");
            setIncludeDeleted(false);
            setPage(1);
          }}
        >
          초기화
        </Button>
      </Space>
      <Table
        rowKey="postId"
        columns={columns}
        dataSource={data?.content}
        loading={isLoading}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: data?.page?.totalElements,
          onChange: setPage,
          showSizeChanger: false,
        }}
        size="small"
      />

      <Drawer
        title={`게시글 상세 #${detail?.postId ?? ""}`}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={680}
      >
        {detail && (
          <>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="게시판">
                {detail.boardCode === "TYPE"
                  ? `유형 (${detail.postTypeName ?? "-"})`
                  : "자유"}
              </Descriptions.Item>
              <Descriptions.Item label="작성자">
                {detail.authorNickname
                  ? `${detail.authorNickname} (#${detail.authorUserId})`
                  : `#${detail.authorUserId}`}
              </Descriptions.Item>
              <Descriptions.Item label="제목" span={2}>
                {detail.title}
              </Descriptions.Item>
              <Descriptions.Item label="내용" span={2}>
                <div style={{ whiteSpace: "pre-wrap" }}>{detail.content}</div>
              </Descriptions.Item>
              <Descriptions.Item label="반응">
                ♥{detail.likeCnt} 👎{detail.dislikeCnt} 💬{detail.commentCnt} 👁
                {detail.viewCnt}
              </Descriptions.Item>
              <Descriptions.Item label="펫 ID">
                {detail.petId ?? "-"}
              </Descriptions.Item>
              <Descriptions.Item label="이미지그룹">
                {detail.fileGrpId ?? "-"}
              </Descriptions.Item>
              <Descriptions.Item label="상태">
                {detail.delYn ? (
                  <Tag color="red">삭제</Tag>
                ) : (
                  <Tag color="green">노출</Tag>
                )}
                {detail.adminLockYn && <Tag color="volcano">잠금</Tag>}
                {!detail.postingYn && <Tag>미게시</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="작성일">
                {detail.createdAt?.replace("T", " ").slice(0, 19)}
              </Descriptions.Item>
              <Descriptions.Item label="수정일">
                {detail.contentModifiedAt
                  ? detail.contentModifiedAt.replace("T", " ").slice(0, 19)
                  : "-"}
              </Descriptions.Item>
            </Descriptions>

            {/* 본문 바로 아래에 스레드 — 무엇에 답하는지 보면서 쓰게 한다 */}
            <Divider titlePlacement="left">댓글</Divider>
            <PostCommentThread
              postId={detail.postId}
              // 아래 "변경 이력"(280) 과 "조치" 버튼이 화면 안에 남도록 잡은 값
              maxHeight={360}
              onChanged={() => {
                mutate();
                mutateDetail();
              }}
            />

            {/* 누가 잠갔다 풀었는지는 플래그만 봐선 알 수 없다 — 되돌리면 흔적이 0 이 된다 */}
            <Divider titlePlacement="left">변경 이력</Divider>
            <AuditLogTimeline
              targetType="POST"
              targetId={detail.postId}
              enabled={drawerOpen}
              // Drawer 안에는 본문·댓글·조치가 이미 다 들어 있다 — 이력이 길어지면
              // 아래 "조치" 버튼이 화면 밖으로 밀린다
              maxHeight={280}
            />

            <Divider titlePlacement="left">조치</Divider>
            <Space wrap>
              <Button
                onClick={() =>
                  navigate(`/community/comments?postId=${detail.postId}`)
                }
              >
                댓글 목록에서 보기
              </Button>
              {detail.adminLockYn ? (
                <Button
                  onClick={() =>
                    act(
                      () => unlockCommunityPost(detail.postId),
                      "잠금 해제됨",
                      detail.postId,
                    )
                  }
                >
                  잠금 해제
                </Button>
              ) : (
                <Button
                  disabled={detail.delYn}
                  onClick={() =>
                    act(() => lockCommunityPost(detail.postId), "잠금됨", detail.postId)
                  }
                >
                  잠금
                </Button>
              )}
              <Popconfirm
                title="게시글을 강제 삭제하시겠습니까?"
                onConfirm={() =>
                  act(
                    () => forceDeleteCommunityPost(detail.postId),
                    "삭제됨",
                    detail.postId,
                  )
                }
              >
                <Button danger disabled={detail.delYn}>
                  강제삭제
                </Button>
              </Popconfirm>
            </Space>
          </>
        )}
      </Drawer>

      <AdminAuthorModal
        open={authorOpen}
        onClose={() => setAuthorOpen(false)}
        onSaved={() => {
          // 관리자명을 바꾸면 과거 댓글 표시명까지 소급 반영된다(스냅샷 아님, 2026-08-04 결정).
          // 열려 있는 댓글 스레드도 맞춰주지 않으면 옛 이름이 그대로 남는다.
          globalMutate(
            (key) => typeof key === "string" && key.includes("/thread"),
          );
        }}
      />
    </div>
  );
};

export default CommunityPostPage;
